<template>
  <div>
    <page-header
      :actions="pageHeaderActionsConfig"
      :page="pageHeaderConfig"
      right-side-class="col-sm-12 col-md-4 align-items-end d-flex pb-2 justify-content-end"
    />
    <board :details="interventionPlanHeaderInfo" />
    <div class="mt-4">
      <section-title has-line title="Historial de Plan Enmendado" />
      <div
        v-for="(item, index) in history"
        :key="`history-item-${index}`"
        class="row bg-secondary-3 rounded ml-2 mr-0 p-3 mb-2"
      >
        <div class="col border-right d-flex align-items-center">
          <div class="color-black font-16 font-weight-bold">
            {{ item.statusName }}
          </div>
        </div>
        <div class="col">
          <div class="color-gray-6 font-14 font-weight-normal">Fecha:</div>
          <div class="color-black font-weight-normal">
            {{ item.date | date }}
          </div>
        </div>
        <div
          class="col-12 col-md-8 mt-3 mt-md-0 d-flex justify-content-end align-items-center"
        >
          <action-button
            class="outline-success px-2"
            i="fas fa-file-search"
            text="Ver Plan"
            variant="outline-success"
            @click="showPlan(item.id, item.academicYearId)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import {
  getPlanInfo as _getPlanInfo,
  getInterventionPlanHistory as _getInterventionPlanHistory,
} from '@/services/uspqrp/intervention-plan.api.js';
import store from '@/store';
import { modulesName } from '@/store';

export default {
  name: 'AmendedPlanHistory',
  components: {
    PageHeader,
    Board,
    SectionTitle,
    ActionButton,
  },
  data() {
    return {
      history: [],
      from: 'interventionPlan',
      headerInfo: {},
    };
  },
  computed: {
    planId() {
      return Number(this.$route.params.planId);
    },
    contractId() {
      return Number(this.$route.params.contractId);
    },

    pageHeaderConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Historial de Plan Enmendado',
        routes: [
          'Inicio',
          'Proveedores',
          'Contrato',
          'Añadir Plan de Intervención',
          'Historial de Plan Enmendado',
        ],
      };
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
      ];
    },
    interventionPlanHeaderInfo() {
      if (!this.isDataReady) {
        return [];
      }
      return [
        [
          {
            label: 'SIE:',
            icon: 'fas fa-address-book',
            data: this.headerInfo?.studentInformation.studentSieId,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Estudiante:',
            icon: 'fas fa-address-book',
            data: this.headerInfo.studentInformation.studentName,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Edad:',
            icon: 'fas fa-calendar-edit',
            data: `${this.headerInfo.studentInformation.age} años`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.headerInfo.studentInformation.impedimentDescription,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Región:',
            icon: 'fas fa-map-marker-alt',
            data: this.headerInfo.studentInformation.regionName,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Municipio:',
            icon: 'fas fa-map-marker-alt',
            data: this.headerInfo.studentInformation.municipalityName,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Código-Escuela:',
            icon: 'fas fa-graduation-cap',
            data: this.headerInfo.studentInformation.schoolName,
            cols: 'col-12 col-md-3',
          },
        ],
        [
          {
            label: 'Nombre de Especialista:',
            data: this.headerInfo.planGeneralInfo.writerSpecialist.fullName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Tipo de Servicio:',
            data: this.headerInfo.studentInformation.serviceTypeDescription,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Modalidad:',
            data: this.headerInfo.studentInformation.serviceInfo.modality,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Frecuencia:',
            data: this.headerInfo.studentInformation.serviceInfo.frequency,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Duración:',
            data: this.headerInfo.studentInformation.serviceInfo.duration,
            cols: 'col-4 col-sm col-12 mt-3',
          },
        ],
      ];
    },
    isDataReady() {
      return (
        this.headerInfo &&
        this.headerInfo.studentInformation &&
        this.headerInfo.studentInformation.serviceInfo &&
        this.headerInfo.planGeneralInfo
      );
    },
  },
  async created() {
    await Promise.all([this.getPlanInfo(), this.getInterventionHistory()]);
  },
  methods: {
    async getPlanInfo() {
      store.dispatch(`${modulesName.appModuleName}/setLoading`, true);

      const response = (await _getPlanInfo(this.contractId, this.planId)).data;
      this.headerInfo = response;
      store.dispatch(`${modulesName.appModuleName}/setLoading`, false);
    },
    async getInterventionHistory() {
      const { data } = await _getInterventionPlanHistory(this.contractId);
      if (data?.length) {
        this.history = data.map((h) => ({
          ...h,
          statusName: h.isAmendment ? 'Enmendado' : 'Sometido',
        }));
      }
    },
    showPlan(planId, academicYear) {
      this.$router.push({
        name: 'interventionPlanManagement',
        params: {
          academicYearId: academicYear,
          planId: planId,
          onlyShowPlan: Number(true),
          from: 'amendedPlanHistory',
          fromData: this.headerInfo,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .btn-view__extended {
  background: transparent !important;
  &:hover {
    color: #2db782 !important;
  }
}
::v-deep.intervention-plan-continer {
  .mobile-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
